import React, { useState, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import { AuthContext } from "./../AuthContext";
import google from "../resource/google.png";
import axios from 'axios';
import API_BASE_URL from "../api";
import {
  Leaf,
  Phone,
  Lock,
  User as UserIcon,
  ArrowRight,
  ArrowLeft,
  ShieldCheck,
  Eye,
  EyeOff,
  Smartphone,
  BarChart3,
  Truck,
  CheckCircle2
} from "lucide-react";

export default function Signup() {
  const { signup, googleLogin } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    userName: "",
    mobileNumber: "",
    pin: "",
    confirmPin: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [showConfirmPin, setShowConfirmPin] = useState(false);

  const [showOtpLogin, setShowOtpLogin] = useState(false);
  const [otpMobile, setOtpMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (formData.pin !== formData.confirmPin) {
      setError("PIN and Confirm PIN do not match");
      return;
    }
    setError("");
    setLoading(true);
    try {
      console.log("formdata", formData)
      await signup(formData);
    } catch (err) {
      setError(err.message || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  const sendOtp = async () => {
    setOtpError("");
    if (!otpMobile) {
      setOtpError("Please enter mobile number");
      return;
    }
    const mobileRegex = /^\d{10}$/;
    if (!mobileRegex.test(otpMobile)) {
      setOtpError("Please enter a valid 10-digit mobile number");
      return;
    }
    setOtpLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/otp/send`, { mobileNumber: otpMobile });
      setOtpSent(true);
    } catch (e) {
      setOtpError(e.response?.data?.message || "Failed to send OTP");
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOtp = async () => {
    setOtpError("");
    if (!otp) {
      setOtpError("Please enter OTP");
      return;
    }
    setOtpLoading(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/otp/verify`, { mobileNumber: otpMobile, otp });
      if (res.data.success) {
        setOtpSent(false);
        alert("OTP verified! Phone number populated into signup form.");
        setFormData({ ...formData, mobileNumber: otpMobile });
        setShowOtpLogin(false);
      } else {
        setOtpError("Invalid OTP");
      }
    } catch (e) {
      setOtpError(e.response?.data?.message || "OTP verification failed");
    } finally {
      setOtpLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-gray-50 flex items-stretch font-sans">
      <div className="w-full grid grid-cols-1 lg:grid-cols-12 min-h-screen">
        {/* Left Side (Text & Branding Showcase) */}
        <div className="lg:col-span-5 hidden lg:flex flex-col justify-between bg-gradient-to-br from-[#0E3B2E] via-[#092b21] to-[#041611] text-white p-12 relative overflow-hidden">
          {/* Subtle Grid Pattern Overlay */}
          <div className="absolute inset-0 bg-[radial-gradient(#10B981_1px,transparent_1px)] [background-size:24px_24px] opacity-10 pointer-events-none" />

          {/* Top Logo */}
          <div className="relative z-10 flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center">
              <Leaf className="w-7 h-7 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white tracking-tight">Pure Prakruti</h2>
              <p className="text-xs text-emerald-200/80">Green Logistics & Sustainability Portal</p>
            </div>
          </div>

          {/* Center Showcase Content */}
          <div className="relative z-10 my-auto py-8">
            <span className="inline-flex items-center gap-1.5 bg-emerald-500/20 border border-emerald-400/30 text-emerald-300 text-xs font-semibold px-3.5 py-1.5 rounded-full mb-6">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
              Join the Eco Fleet Movement
            </span>
            <h1 className="text-4xl font-extrabold text-white leading-tight tracking-tight mb-4">
              Register Your Transport Account
            </h1>
            <p className="text-emerald-100/80 text-sm leading-relaxed mb-8 max-w-md">
              Create an account to track carbon emissions, generate PDF certificates, manage fleet compliance, and access route analytics.
            </p>

            <div className="space-y-4">
              <div className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-2xl p-4 backdrop-blur-sm">
                <BarChart3 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-semibold text-white">Instant Account Activation</h4>
                  <p className="text-xs text-emerald-200/70 mt-0.5">Quick setup with mobile PIN and 1-time KYC verification support.</p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-2xl p-4 backdrop-blur-sm">
                <Truck className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-semibold text-white">Full Dashboard Access</h4>
                  <p className="text-xs text-emerald-200/70 mt-0.5">View top emitting vehicles, total distance, and downloadable CSV reports.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Footer Stat */}
          <div className="relative z-10 pt-6 border-t border-emerald-800/40 flex items-center justify-between text-xs text-emerald-200/70">
            {/* <span className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
             
            </span> */}
            <span>© 2026 Pure Prakruti</span>
          </div>
        </div>

        {/* Right Side (Form Container) */}
        <div className="lg:col-span-7 flex flex-col justify-center items-center p-6 sm:p-12 bg-gray-50">
          {/* Mobile Top Branding */}
          <div className="lg:hidden flex items-center gap-3 mb-8 text-center">
            <div className="w-10 h-10 rounded-xl bg-[#0E3B2E] text-emerald-400 flex items-center justify-center">
              <Leaf className="w-6 h-6" />
            </div>
            <div className="text-left">
              <h2 className="text-lg font-bold text-gray-900">Pure Prakruti</h2>
              <p className="text-xs text-gray-500">Green Logistics Portal</p>
            </div>
          </div>

          {/* Form Card Container */}
          <div className="w-full max-w-md bg-white rounded-3xl border border-gray-200/80 shadow-xl p-8 sm:p-10">
            <div className="mb-6">
              <Link to="/" className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-emerald-800 transition mb-4 group">
                <ArrowLeft className="w-4 h-4 text-gray-400 group-hover:-translate-x-0.5 transition-transform" />
                <span>Back to Main Website</span>
              </Link>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
                Sign Up
              </h2>
              <p className="text-xs sm:text-sm text-gray-500 mt-1">
                Enter your details to create your Pure Prakruti user account.
              </p>
            </div>

            {error && (
              <div className="mb-5 p-3.5 rounded-2xl bg-red-50 border border-red-200 text-red-700 text-xs font-semibold">
                {error}
              </div>
            )}

            <form onSubmit={handleSignup} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                  Full Name
                </label>
                <div className="relative flex items-center">
                  <UserIcon className="w-4 h-4 text-gray-400 absolute left-4 pointer-events-none" />
                  <input
                    type="text"
                    name="userName"
                    required
                    value={formData.userName}
                    onChange={handleChange}
                    placeholder="Enter full name"
                    className="w-full bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 text-sm rounded-xl pl-11 pr-4 py-3 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                  Phone Number
                </label>
                <div className="relative flex items-center">
                  <Phone className="w-4 h-4 text-gray-400 absolute left-4 pointer-events-none" />
                  <input
                    type="text"
                    name="mobileNumber"
                    required
                    value={formData.mobileNumber}
                    onChange={handleChange}
                    placeholder="Enter 10-digit phone number"
                    className="w-full bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 text-sm rounded-xl pl-11 pr-4 py-3 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                    Create PIN
                  </label>
                  <div className="relative flex items-center">
                    <Lock className="w-4 h-4 text-gray-400 absolute left-4 pointer-events-none" />
                    <input
                      type={showPin ? "text" : "password"}
                      name="pin"
                      required
                      value={formData.pin}
                      onChange={handleChange}
                      placeholder="PIN"
                      className="w-full bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 text-sm rounded-xl pl-11 pr-10 py-3 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPin(!showPin)}
                      className="absolute right-3 text-gray-400 hover:text-gray-600 transition"
                    >
                      {showPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">
                    Confirm PIN
                  </label>
                  <div className="relative flex items-center">
                    <Lock className="w-4 h-4 text-gray-400 absolute left-4 pointer-events-none" />
                    <input
                      type={showConfirmPin ? "text" : "password"}
                      name="confirmPin"
                      required
                      value={formData.confirmPin}
                      onChange={handleChange}
                      placeholder="Confirm PIN"
                      className="w-full bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 text-sm rounded-xl pl-11 pr-10 py-3 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPin(!showConfirmPin)}
                      className="absolute right-3 text-gray-400 hover:text-gray-600 transition"
                    >
                      {showConfirmPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#0E3B2E] hover:bg-emerald-800 text-white font-bold text-base py-3.5 rounded-xl shadow-md transition transform hover:scale-[1.01] flex items-center justify-center gap-2 mt-2 disabled:opacity-50"
              >
                {loading ? (
                  <span>Creating Account...</span>
                ) : (
                  <>
                    Register Account <ArrowRight className="w-5 h-5 text-emerald-400" />
                  </>
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="relative my-6 text-center">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <span className="relative bg-white px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Or Continue With
              </span>
            </div>

            <div className="space-y-3">
              <button
                type="button"
                onClick={googleLogin}
                className="w-full bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 font-semibold py-3 rounded-xl transition flex items-center justify-center gap-3 text-sm shadow-sm"
              >
                <img src={google} alt="Google" className="w-5 h-5 shrink-0" />
                <span>Sign Up with Google</span>
              </button>

              <button
                type="button"
                onClick={() => setShowOtpLogin(!showOtpLogin)}
                className="w-full bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-semibold py-3 rounded-xl border border-emerald-200 transition flex items-center justify-center gap-2 text-sm"
              >
                <Smartphone className="w-4 h-4 text-emerald-700" />
                <span>{showOtpLogin ? "Hide OTP Form" : "Verify Phone via Mobile OTP"}</span>
              </button>
            </div>

            {/* Expandable OTP Signup */}
            {showOtpLogin && (
              <div className="mt-4 p-4 rounded-2xl bg-gray-50 border border-gray-200 space-y-3">
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Mobile OTP Verification
                </label>
                <input
                  type="text"
                  placeholder="Enter 10-digit mobile number"
                  value={otpMobile}
                  onChange={(e) => setOtpMobile(e.target.value)}
                  className="w-full bg-white border border-gray-200 text-gray-900 placeholder-gray-400 text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                {!otpSent ? (
                  <button
                    type="button"
                    onClick={sendOtp}
                    disabled={otpLoading}
                    className="w-full bg-[#0E3B2E] hover:bg-emerald-800 text-white font-bold py-2.5 rounded-xl text-sm transition"
                  >
                    {otpLoading ? "Sending..." : "Send Verification OTP"}
                  </button>
                ) : (
                  <>
                    <input
                      type="text"
                      placeholder="Enter OTP code"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      className="w-full bg-white border border-gray-200 text-gray-900 placeholder-gray-400 text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                    <button
                      type="button"
                      onClick={verifyOtp}
                      disabled={otpLoading}
                      className="w-full bg-[#0E3B2E] hover:bg-emerald-800 text-white font-bold py-2.5 rounded-xl text-sm transition"
                    >
                      {otpLoading ? "Verifying..." : "Verify OTP Code"}
                    </button>
                  </>
                )}
                {otpError && <p className="text-red-600 text-xs font-semibold">{otpError}</p>}
              </div>
            )}

            {/* Link to Login */}
            <div className="mt-8 text-center pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-600">
                Already have an account?{" "}
                <Link to="/login" className="text-emerald-700 hover:text-emerald-900 font-bold underline ml-1">
                  Log In
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}